/**
 * The Data team's merchandising badges, one boolean per badge.
 *
 * Names match UPS's GraphQL `Product.attributes` fields, which are also the
 * values in the feed's `merchFlags` attribute. `newToSale` and `furtherReduced`
 * are only ever set while the product is on sale.
 */
export interface MerchFlags {
  bestseller: boolean;
  furtherReduced: boolean;
  mostLoved: boolean;
  newToSale: boolean;
  sellingFast: boolean;
}

/**
 * Reads the `merchFlags` attribute, a text attribute listing the badges live on
 * the product, e.g. `{ text: ['bestseller', 'sellingFast'] }`. A product with no
 * live badge has no attribute at all.
 *
 * Typed loosely so that both the listings and the recommendations responses can
 * pass their attribute maps: the two type `text` differently, and neither is
 * assignable to the other. Anything absent or in an unexpected shape reads as no
 * badges rather than throwing, since a badge is not worth a broken page.
 */
export const readMerchFlags = (
  attributes: Readonly<Record<string, unknown>> | null | undefined,
): MerchFlags => {
  const text = (attributes?.merchFlags as { text?: unknown } | null | undefined)?.text;
  const live = new Set<unknown>(Array.isArray(text) ? text : []);

  return {
    bestseller: live.has('bestseller'),
    furtherReduced: live.has('furtherReduced'),
    mostLoved: live.has('mostLoved'),
    newToSale: live.has('newToSale'),
    sellingFast: live.has('sellingFast'),
  };
};
